/**
 * Documents page (FusionDocs inside ONE): shapes of the /api/fd/* answers, query keys and small helpers.
 * Everything is same-origin; ONE asks FusionDocs as the signed-in person.
 */
import { useEffect, useState } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../../lib/api";

export type AccessMode = "roles" | "owner" | "listed";
export interface FdAccess { mode: AccessMode | string; members?: string[] | null }
export interface FdVersionInfo { id?: string; mimeType?: string; sizeBytes?: number; versionNo?: number }
export interface FdDoc {
  id: string; title: string; type?: string; company?: string; filingPath?: string; confidentiality?: string;
  access?: FdAccess; updatedAt?: string; url?: string; currentVersion?: FdVersionInfo | null;
  /** Search hits only. */
  snippet?: string;
}
export interface FdFolder { path: string; name: string; count?: number; hasChildren?: boolean }
export interface FdFiled { id: string; title: string; url: string | null; status: string; filedAt: string }
export interface FdHome { browse: boolean; folders: FdFolder[]; unfiled?: number; total?: number | null; recent: FdDoc[] | null; filedFromOne: FdFiled[] }
export interface FdFolders { browse: boolean; items: FdFolder[]; unfiled?: number }
export interface FdList { browse: boolean; items: FdDoc[]; nextCursor: string | null; total?: number | null }
export interface FdVersion { id: string; versionNo: number; mimeType?: string; sizeBytes?: number; createdAt?: string }
export interface FdDetail {
  document: FdDoc & { lifecycleState?: string; source?: { app?: string; label?: string; url?: string } | null };
  versions: FdVersion[] | null;
}
export interface FdHit { id: string; title: string; type?: string; snippet?: string; summary?: string; url?: string; updatedAt?: string; filingPath?: string }
export interface FdObligation { id: string; kind?: string; dueAt: string; noticePeriod?: string; documentId?: string; documentTitle?: string; url?: string }
export interface FdAnswer { answer: string; found: boolean; sources: Array<{ n: number; title: string; url?: string; snippet?: string }> }
export interface FdFileResult { documentId: string | null; url: string | null; status: string; companyNotFound?: string | null }

export const FK = {
  home: ["fd", "home"] as const,
  folders: (parent: string) => ["fd", "folders", parent] as const,
  list: (p: Record<string, string>) => ["fd", "list", p] as const,
  search: (q: string) => ["fd", "search", q] as const,
  doc: (id: string) => ["fd", "doc", id] as const,
  obligations: ["fd", "obligations"] as const,
};

export const FD_MAX = 99 * 1024 * 1024;

export const isOff = (e: unknown) => e instanceof ApiError && e.code === "fusiondocs_off";
export const errText = (e: unknown) => (e instanceof ApiError ? e.message : e instanceof Error ? e.message : "Something went wrong");

/** Rename or move failures, said plainly; 403 means no filing rights on this document. */
export const editErrText = (e: unknown) =>
  e instanceof ApiError && e.status === 403
    ? `You don't have filing rights for this document${e.message && !/^forbidden$/i.test(e.message) ? ` (${e.message})` : ""}.`
    : errText(e);

/** "/ Finance / Invoices/" → "Finance/Invoices". */
export const cleanPath = (p: string) => p.split("/").map((s) => s.trim()).filter(Boolean).join("/");

export const folderUrl = (parent?: string) => `/api/fd/folders${parent ? `?parent=${encodeURIComponent(parent)}` : ""}`;
export const thumbUrl = (id: string) => `/api/fd/documents/${encodeURIComponent(id)}/preview`;

export const patchDoc = (id: string, json: { title?: string; filingPath?: string }) =>
  api<{ document: FdDoc }>(`/api/fd/documents/${encodeURIComponent(id)}`, { method: "PATCH", json });

/** After a rename or move: show the returned document now, then refresh lists, folders and counts. */
export function afterEdit(qc: QueryClient, id: string, doc?: FdDoc) {
  if (doc) qc.setQueryData<FdDetail>(FK.doc(id), (old) => (old ? { ...old, document: { ...old.document, ...doc } } : old));
  for (const k of [["fd", "list"], ["fd", "folders"], FK.home, FK.doc(id)]) void qc.invalidateQueries({ queryKey: k });
}

export const humanSize = (n?: number) =>
  n == null ? "" : n >= 1e9 ? `${(n / 1e9).toFixed(1)} GB` : n >= 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1e3))} KB`;
export const day = (v?: string | null) => (v ? new Date(v).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");

export function contentUrl(id: string, o: { download?: boolean; version?: number } = {}) {
  const q = new URLSearchParams();
  if (o.download) q.set("download", "1");
  if (o.version) q.set("version", String(o.version));
  const s = q.toString();
  return `/api/fd/documents/${encodeURIComponent(id)}/content${s ? `?${s}` : ""}`;
}

/** A FusionDocs link (e.g. https://dms…/documents/<id>) → the document id, when the link carries one. */
export function idFromUrl(url?: string | null): string | null {
  if (!url) return null;
  try {
    const u = new URL(url, window.location.origin);
    const m = /\/documents?\/([A-Za-z0-9_-]{6,100})(?:[/?#]|$)/.exec(u.pathname)
      ?? /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i.exec(u.pathname + u.search);
    return m?.[1] ?? u.searchParams.get("document") ?? u.searchParams.get("id");
  } catch { return null; }
}

/** How the bytes can be shown in the browser; the API serves only these inline. */
export function previewKind(mime?: string): "pdf" | "image" | "video" | "audio" | "text" | null {
  if (!mime) return null;
  if (mime === "application/pdf") return "pdf";
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/")) return "audio";
  if (mime.startsWith("text/plain")) return "text";
  return null;
}

/** Three panes from 1024 px up; below that the preview takes the whole screen. */
export function useWide() {
  const q = "(min-width: 1024px)";
  const [wide, setWide] = useState(() => typeof window !== "undefined" && window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const on = () => setWide(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return wide;
}

export function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}
